/**
 * ReferenceCard Component
 * Displays a reference image with description and trigger word status
 */

import { X, AlertTriangle, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ReferenceImage } from "../types";

interface ReferenceCardProps {
  reference: ReferenceImage;
  index: number;
  onRemove: () => void;
  onToggleTriggerWord: () => void;
}

export function ReferenceCard({
  reference,
  index,
  onRemove,
  onToggleTriggerWord,
}: ReferenceCardProps) {
  const charCount = reference.description.length;

  return (
    <div className="rounded-[8px] overflow-hidden bg-[#1a1a1a] border border-white/10">
      {/* Image with badges */}
      <div className="relative aspect-square">
        <img
          src={reference.previewUrl}
          alt={`Reference ${index + 1}`}
          className="w-full h-full object-cover"
        />

        {/* Number badge */}
        <div className="absolute top-2 left-2 w-6 h-6 rounded-[4px] bg-black/70 backdrop-blur-sm flex items-center justify-center">
          <span className="text-[10px] font-mono text-white/90">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>

        {/* Delete button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute top-2 right-2 w-6 h-6 rounded-[4px] bg-black/70 backdrop-blur-sm flex items-center justify-center hover:bg-red-500/80 transition-colors"
        >
          <X className="w-3 h-3 text-white/90" />
        </button>

        {/* Upscale button - shown when image needs upscaling */}
        {reference.needsUpscale && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // TODO: Trigger upscale action
                }}
                className="absolute bottom-2 right-2 w-6 h-6 rounded-[4px] bg-amber-500/90 backdrop-blur-sm flex items-center justify-center hover:bg-amber-400 transition-colors"
              >
                <Maximize2 className="w-3 h-3 text-black" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Upscale to 1024×1024 for best results
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Description preview */}
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2 leading-relaxed">
          {reference.description}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between">
          {/* Char count */}
          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">
            {charCount} chars
          </span>

          {/* Trigger word button */}
          {!reference.hasTriggerWord && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleTriggerWord();
              }}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium",
                "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 transition-colors",
              )}
            >
              <AlertTriangle className="w-3 h-3" />
              ADD TRIGGER
            </button>
          )}

          {reference.hasTriggerWord && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleTriggerWord();
              }}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium",
                "bg-green-500/10 text-green-500 hover:bg-green-500/20 transition-colors",
              )}
            >
              TRIGGER ADDED
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
